import { cva } from "class-variance-authority";
import { ReactNode } from "react";

// Pure typography styles - no semantic meaning
const typography = cva([], {
  variants: {
    size: {
      homepageH1: [
        "text-[2.5rem] leading-[3.5rem] sm:text-[3.375rem] sm:leading-[4.3875rem]",
      ],
      h1: ["sm:text-[4.25rem] sm:leading-[107%] text-[2rem] leading-[140%]"],
      h1Promo: ["text-[2rem] leading-[2.1rem] md:text-[4.25rem] md:leading-20"],
      careerHeroH1: ["text-[2.25rem] lg:text-[4.25rem] leading-[4.388rem]"],
      brandH1: [""],
      faqH1: ["sm:text-[4.25rem] sm:leading-[107%] text-[2rem] leading-[140%]"],
      h2: [
        "sm:text-[3.375rem] leading-[130%] text-[2rem] transition-text duration-300 ease-in-out",
      ],
      h2LandingC: [
        "sm:text-[3.375rem] leading-[130%] text-[36px] transition-text duration-300 ease-in-out",
      ],
      unresponsiveH2: ["text-[3.375rem] leading-[130%]"],
      h2CheckList: [
        "sm:text-[3.375rem] leading-[130%] sm:leading-[140%] text-[1.5rem] transition-text duration-300 ease-in-out",
      ],
      h3: ["sm:text-[2.5rem] sm:leading-[3.75rem] text-2xl leading-[1.875rem]"],
      h4Promo: [
        "!text-2xl !leading-[140%] md:!text-[2rem] md:leading-[2.1rem]",
      ],
      h4: ["sm:text-[2rem] leading-[140%]  text-[1.5rem] "],
      h5: ["sm:text-[1.5rem] sm:leading-[140%] text-[1.25rem] leading-[150%]"],
      unresponsiveH5: ["!text-[1.5rem] !leading-[150%]"],
      h6: [
        "sm:text-xl text-base leading-[150%] transition-text duration-300 ease-in-out",
      ], //Goes from 20px to 16
      unresponsiveH6: ["!text-[1.25rem] !leading-[150%]"],
      brandsH6: ["text-[1.25rem] leading-[150%]"],
      h6_body2: ["sm:text-[1.25rem] leading-[150%] text-[.875rem] "],
      h2Popup: [
        "text-[2.75rem] leading-[120%] md:text-[2.75rem] md:leading-[120%]",
      ],
      exotic_h5_body2: ["text-[1rem] lg:text-[1.5rem] leading-[150%]"],

      navbarDesktopLinks: ["text-[1.125rem] leading-[150%]"],
      navbarMobileLinks: [],
    },
    weight: {
      300: ["font-light"],
      400: ["font-normal"],
      500: ["font-medium"],
      600: ["font-semibold"],
      700: ["font-bold"],
    },
    color: {
      white: ["text-white"],
      black: ["text-black"],
      copyGray: ["text-copy-gray"],
      darkGray: ["text-dark-gray"],
      lightGray: ["text-lightGray"],
    },
  },
  defaultVariants: {
    size: "h6",
    weight: 400,
  },
});

export interface BaseProps {
  children?: ReactNode;
  dangerouslySetInnerHTML?: { __html: string };
  size?:
    | "homepageH1"
    | "h1"
    | "h2"
    | "unresponsiveH2"
    | "h3"
    | "h4"
    | "h5"
    | "h6"
    | "brandsH6"
    | "h6_body2"
    | "faqH1"
    | "careerHeroH1"
    | "h1Promo"
    | "h4Promo"
    | "unresponsiveH5"
    | "unresponsiveH6"
    | "h2CheckList"
    | "h2Popup"
    | "navbarDesktopLinks"
    | "navbarMobileLinks"
    | "exotic_h5_body2"
    | "h2LandingC";
  weight?: 300 | 400 | 500 | 600 | 700;
  color?: "white" | "black" | "copyGray" | "darkGray" | "lightGray";
  className?: string;
  as: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "li" | "span";
  dataTestId?: string;
}

// Semantic heading components - SEO friendly
export const Heading = ({
  children,
  size = "h6",
  weight,
  color,
  className,
  as = "h6",
  dangerouslySetInnerHTML,
  dataTestId,
}: BaseProps) => {
  // When injecting raw HTML via dangerouslySetInnerHTML, phrasing-content
  // wrappers (<p>, <span>, <li>) cannot legally contain block elements like
  // <p> or <ul> — which CMS WYSIWYG output almost always produces. The
  // browser auto-corrects the invalid nesting, breaking React hydration
  // (React error #418). Force <div> in those cases.
  const Component =
    dangerouslySetInnerHTML && (as === "p" || as === "span" || as === "li")
      ? "div"
      : as;
  if (dangerouslySetInnerHTML) {
    return (
      <Component
        data-testid={dataTestId ?? ""}
        className={`${typography({ size, weight, color })} ${className ?? ""}`}
        dangerouslySetInnerHTML={dangerouslySetInnerHTML}
      ></Component>
    );
  }
  return (
    <Component
      data-testid={dataTestId ?? ""}
      className={`${typography({ size, weight, color })} ${className ?? ""}`}
    >
      {children && children}
    </Component>
  );
};
